import { gql } from "@apollo/client";
import { Rule } from "@asmbl/shared/eligibility";
import { CircularProgress } from "@mui/material";
import makeStyles from "../../../makeStylesV4Compat";
import { EligibilityEditor_employee as Employee } from "src/__generated__/graphql";
import { MultiRuleEditor } from "./MultiRuleEditor";
import { ParticipantsLoadingBoundary } from "./ParticipantsLoadingBoundary";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
  },
}));

type Props = {
  compCycleId: number;
  ruleSet: Rule[];
  employees: Employee[];
  onChange: (rules: Rule[]) => Promise<void>;
  isChangeLoading: boolean;
  departmentOptions?: { label: string; value: number }[];
  ladderOptions?: { label: string; value: number }[];
  levelOptions?: { label: string; value: number }[];
  perfRatingOptions?: { label: string; value: string }[];
  marketOptions?: { label: string; value: number }[];
  locationGroupOptions?: { label: string; value: number }[];
  loading: boolean;
};

export function EligibilityEditor({
  compCycleId,
  ruleSet,
  employees,
  onChange,
  isChangeLoading,
  departmentOptions,
  ladderOptions,
  levelOptions,
  perfRatingOptions,
  marketOptions,
  locationGroupOptions,
  loading,
}: Props): JSX.Element {
  const classes = useStyles();

  const handleChange = async (newRules: Rule[]) => {
    await onChange(newRules);
  };

  return (
    <div className={classes.root}>
      {loading ? (
        <CircularProgress />
      ) : (
        <MultiRuleEditor
          ruleSet={ruleSet}
          onChange={handleChange}
          employees={employees}
          departmentOptions={departmentOptions}
          ladderOptions={ladderOptions}
          levelOptions={levelOptions}
          perfRatingOptions={perfRatingOptions}
          marketOptions={marketOptions}
          locationGroupOptions={locationGroupOptions}
        />
      )}
      {isChangeLoading ? (
        <CircularProgress />
      ) : (
        <ParticipantsLoadingBoundary compCycleId={compCycleId} />
      )}
    </div>
  );
}

EligibilityEditor.fragments = {
  organization: gql`
    ${MultiRuleEditor.fragments.organization}
    fragment EligibilityEditor_organization on Organization {
      id
      ...MultiRuleEditor_organization
    }
  `,
  compCycle: gql`
    ${MultiRuleEditor.fragments.compCycle}
    fragment EligibilityEditor_compCycle on CompCycle {
      ...MultiRuleEditor_compCycle
    }
  `,
  compCycle2: gql`
    ${MultiRuleEditor.fragments.compCycle2}
    fragment EligibilityEditor_compCycle2 on CompCycle2 {
      ...MultiRuleEditor_compCycle2
    }
  `,
  employee: gql`
    ${MultiRuleEditor.fragments.employee}
    fragment EligibilityEditor_employee on Employee {
      ...MultiRuleEditor_employee
    }
  `,
};
